import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator } from 'react-native';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchCommunityResources } from '@/store/slices/residents/homeSlice';
import { useResponsive } from '@/hooks/common/useResponsive';
import { EmptyState } from '@/components/common/residents/EmptyState';
import { CommunityResource } from '@/types/residents/communityResource';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { ResourceTile } from '@/components/residents/resources/ResourceTile';
import { ResourcePanel } from '@/components/residents/resources/ResourcePanel';
import { strings } from '@/constants/residents';

const GRID_GAP = 16;

export const DesktopCommunityResourcesView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { layoutMode, width } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const numColumns = width >= 768 ? 3 : 2; // Match web: 2 cols small, 3 cols md+

  const resources = useAppSelector((s) => s.home.resources);
  const isLoading = useAppSelector((s) => s.home.isLoading);
  const propertyId = useAppSelector((s) => s.auth.user?.propertyId);

  const [selected, setSelected] = useState<CommunityResource | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  // Ensure resources are loaded for desktop context with a light-weight thunk
  useEffect(() => {
    if (propertyId && (!resources || resources.length === 0)) {
      dispatch(fetchCommunityResources(propertyId));
    }
  }, [dispatch, propertyId]);

  const data = useMemo(() => resources || [], [resources]);

  const handleSelect = useCallback((resource: CommunityResource) => {
    setSelected(resource);
    setPanelOpen(true);
  }, []);

  const renderTile = useCallback(({ item }: { item: CommunityResource }) => (
    <ResourceTile item={item} onPress={handleSelect} />
  ), [handleSelect]);

  if (!isDesktop) {
    // Defensive: this screen is meant for desktop layout via DesktopNavigator
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.mobileFallback}>This view is optimized for desktop layout.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ResourcePanel resource={selected} open={panelOpen} onClose={() => setPanelOpen(false)} />

      {isLoading && data.length === 0 && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" />
        </View>
      )}

      {!isLoading && data.length === 0 && (
        <View style={styles.emptyWrap}>
          <EmptyState
            title={strings.communityResources.empty.title}
            description={strings.communityResources.empty.description}
            iconName="document-outline"
          />
        </View>
      )}

      {!isLoading && data.length > 0 && (
        <FlatList
          data={data}
          keyExtractor={(item) => String(item.id)}
          numColumns={numColumns}
          key={numColumns}
          columnWrapperStyle={styles.columnWrapper}
          contentContainerStyle={styles.listContent}
          renderItem={renderTile}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  listContent: {
    padding: 24,
    paddingTop: 16,
    gap: GRID_GAP,
  },
  columnWrapper: {
    gap: GRID_GAP,
  },
  // Styles for tile and panel are now provided by extracted components
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyWrap: {
    flex: 1,
  },
  mobileFallback: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
});

export default DesktopCommunityResourcesView;


